import { ChevronRight, RefreshCw, Sparkles } from 'lucide-react';
import type { TaskAiSummary as Summary } from '@shared/types/tasks';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { formatRelative } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import { usePref } from '@/hooks/use-pref';
import { UI_PREFS } from '@/lib/ui-prefs';

/** The card's hover text for its AI icon: both sentences. */
export function taskSummaryText(summary: Summary): string {
  return `${summary.what} ${summary.status}`;
}

/**
 * A task's AI summary, above its description: two sentences for someone who hasn't followed it, when
 * it was made, a spinner while a newer one is due, and Summarise again. Clicking its header folds it to
 * the first sentence on one line (SF-T-305); open or folded is remembered in the browser, for every task.
 */
export function TaskAiSummary({
  summary,
  dueAt,
  onSummarise,
  summarising = false,
  className,
}: {
  summary: Summary | null;
  /** When a newer summary is due (activity since this one); null when it's up to date. */
  dueAt: string | null;
  onSummarise?: () => void;
  summarising?: boolean;
  className?: string;
}) {
  const updating = Boolean(dueAt) && !summarising;
  const [open, setOpen] = usePref(UI_PREFS.tasksAiSummaryOpen);
  const folded = Boolean(summary) && !open;
  return (
    <section aria-label="AI summary" className={cn('rounded-md border border-ai-border bg-ai-soft px-3 py-2 text-sm', className)}>
      <div className="flex min-h-6 items-center gap-1.5 whitespace-nowrap text-xs">
        <button
          type="button"
          className="-my-1 flex min-w-0 flex-1 items-center gap-1.5 self-stretch rounded-xs text-left focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-hidden disabled:cursor-default max-md:min-h-11"
          aria-expanded={summary ? open : undefined}
          disabled={!summary}
          onClick={() => setOpen(!open)}
          data-hint={summary ? (open ? 'Fold the summary to one line' : 'Show the whole summary') : undefined}
        >
          {summary ? (
            <ChevronRight className={cn('size-3.5 shrink-0 text-ai transition-transform', open && 'rotate-90')} aria-hidden />
          ) : null}
          <Sparkles className="size-3.5 shrink-0 text-ai" aria-hidden />
          <span className="font-medium text-ai-strong">AI summary</span>
          {summary ? <span className="text-2xs text-muted-foreground">· {formatRelative(summary.made_at)}</span> : null}
        </button>
        {updating ? (
          // Just the spinner: a narrow pane has no room for the words beside Summarise again.
          <span className="inline-flex items-center text-muted-foreground" role="status" data-hint="Updating: there's been activity since this summary">
            <Spinner size="2xs" />
            <span className="sr-only">Updating…</span>
          </span>
        ) : null}
        {onSummarise ? (
          <Button
            variant="ghost"
            size="xs"
            // Tasks is a phone page: a 44px tap target there (forms-and-inputs.project.mdc, "Phone pages").
            className="max-md:h-11"
            loading={summarising}
            loadingText="Summarising…"
            onClick={onSummarise}
            data-hint="Write a new summary now"
          >
            <RefreshCw />
            {summary ? 'Summarise again' : 'Summarise'}
          </Button>
        ) : null}
      </div>
      {summary ? (
        folded ? (
          // The first sentence, cut to one line; the header's button opens it (this is only a bigger target).
          <p className="mt-0.5 cursor-pointer truncate leading-relaxed" onClick={() => setOpen(true)}>
            {summary.what}
          </p>
        ) : (
          <p className="mt-0.5 leading-relaxed">{taskSummaryText(summary)}</p>
        )
      ) : (
        <p className="mt-0.5 text-muted-foreground">{dueAt ? 'The first summary is on its way.' : 'No summary yet.'}</p>
      )}
    </section>
  );
}
